define((require) => {
    "use strict";

    const Backbone         = require("backbone");
    const EmailUtil        = require("util/EmailUtil");
    const ImprovedView     = require("editor/zbone/ImprovedView");
    const Login            = require("security/Login");
    const template         = require("ui/user/ForgotPasswordView.tpl");
    const SequentialButton = require("util/SequentialButton");
    const ThrottleView     = require("ui/common/ThrottleView");
    const ValidateBehavior = require("ui/behaviors/ValidateBehavior");

    const SHAKE_EFFECT_CLASS_NAME = "shake";

    /**
     * The sign in view.
     *
     * @class SignInView
     * @constructor
     *
     * @cfg {String} email The (read-only) username/email possibly entered in the login view.
     */
    return ImprovedView.extend({

        /**
         * @private @type {SequentialButton}
         * The sequential button guarding (throttled) access submitting forgot password requests.
         */
        _action: null,
        /**
         * @private @type {ThrottleView}
         * The throttle view handling lockout and displaying lockout messages.
         */
        _throttleView: null,

        template,

        className: "forgot-password-page pt-page",

        regions: {
            throttleRegion: ".throttle-region",
            defaultFooterRegion: ".default-footer"
        },

        ui: {
            emailInput:     ".email-input",
            forgotForm:     ".forgot-form",
            signInButton:   ".sign-in-button",
            innerContainer: ".inner-container",
            sendSuccess:    ".send-success",
            backLink:       ".back-home"
        },

        events: {
            "submit @ui.forgotForm":  "_forgotFormSubmitHandler",
            "focus @ui.emailInput":   "_emailHandler",
            "keydown @ui.emailInput": "_emailHandler",
            "click @ui.backLink":   "_backLinkClickHandler"
        },

        transitions: {
            "in": "pt-page-scaleUp",
            out: "pt-page-scaleDown"
        },

        behaviors: {
            validateBehavior: {
                behaviorClass: ValidateBehavior,
                buttonSubmit: ".sign-in-button",
                inputValidation: {
                    ".email-input": {
                        validator(email) {
                            return EmailUtil.validateEmail(email);
                        },
                        errorText(email) {
                            if (!email) {
                                return "Email required";
                            } else if (!EmailUtil.validateEmail(email)) {
                                return "Not a valid email";
                            }

                            return "";
                        }
                    }
                }
            }
        },

        _backLinkClickHandler(event) {
            event.preventDefault();
            Backbone.history.history.back();
        },

        _forgotFormSubmitHandler(event) {
            // Stop form from submitting normally
            event.preventDefault();
            this._action();
            return false;
        },

        _emailHandler() {
            this.ui.sendSuccess.hide();
        },

        /**
         * @private
         * Submits the forgot password request if and only if the currently entered email is valid.
         *
         * @return {Promise|Boolean} A promise that will resolve in a success message; false will be returned
         *                           immediately if the current email is not valid.
         */
        _forgot() {
            // Sanity check - bail out early...
            if (!this.inputValidation()) {
                return false;
            }

            const {sendSuccess, innerContainer, emailInput} = this.ui;

            sendSuccess.hide();
            innerContainer.removeClass(SHAKE_EFFECT_CLASS_NAME);

            const email = emailInput.val();

            // Request forgot password mail...
            const promise = Login.forgotPassword(email)
                .done(() => {
                    sendSuccess.show();
                })
                .fail(() => {
                    innerContainer.addClass(SHAKE_EFFECT_CLASS_NAME);
                });

            // Check the response for throttling status code...
            return this._throttleView.checkPromise(email, promise);
        },

        onRender() {
            const {signInButton, emailInput} = this.ui;

            this._action = SequentialButton.add(signInButton, this._forgot.bind(this), true, true);

            // If we have a username set, make it fixed, e.g. for a user already logged in. Does not make sense to
            // allow the user to enter a new username (even though the frontend supports it)...
            if (this.options.username) {
                emailInput.val(this.options.username);
                emailInput.attr("disabled", true);
            }

            // Throttle view...
            this._throttleView = new ThrottleView({
                name:     "forgot",
                target:   emailInput,              // entered email = target
                validate: EmailUtil.validateEmail, // make sure we only throttle for valid emails, will not submit otherwise
                button:   this._action             // disable button during lockout
            });
            this.throttleRegion.show(this._throttleView);
        }

    });
});



// WEBPACK FOOTER //
// ./src/editor/ui/user/ForgotPasswordView.js